import proj4 from 'proj4';

export const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Definiciones de Proj4 para los sistemas de coordenadas
// Estas son definiciones estándar, pero pueden requerir ajustes finos
// o definiciones más específicas para casos de uso muy precisos.
// Se definen una sola vez para evitar redefiniciones en cada llamada.
proj4.defs("EPSG:4326", "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs"); // WGS84 (Lat/Lon)

// ETRS89 UTM Zones (EPSG:258XX)
proj4.defs("EPSG:25827", "+proj=utm +zone=27 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:25828", "+proj=utm +zone=28 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:25829", "+proj=utm +zone=29 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:25830", "+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:25831", "+proj=utm +zone=31 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:25832", "+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");

// ED50 (EPSG:4230 para Lat/Lon, o EPSG:230XX para UTM)
proj4.defs("EPSG:4230", "+proj=longlat +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +no_defs");

// ED50 UTM Zones (EPSG:230XX)
proj4.defs("EPSG:23027", "+proj=utm +zone=27 +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:23028", "+proj=utm +zone=28 +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:23029", "+proj=utm +zone=29 +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:23030", "+proj=utm +zone=30 +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:23031", "+proj=utm +zone=31 +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +units=m +no_defs");
proj4.defs("EPSG:23032", "+proj=utm +zone=32 +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +units=m +no_defs");

// Web Mercator (EPSG:3857)
proj4.defs("EPSG:3857", "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs");

// Otros sistemas geodésicos comunes (Lat/Lon)
proj4.defs("EPSG:4269", "+proj=longlat +ellps=GRS80 +datum=NAD83 +no_defs"); // NAD83
proj4.defs("EPSG:4283", "+proj=longlat +ellps=GRS80 +datum=GDA94 +no_defs"); // GDA94
proj4.defs("EPSG:5326", "+proj=longlat +ellps=GRS80 +datum=ITRF2008 +no_defs"); // ITRF2008
proj4.defs("EPSG:4284", "+proj=longlat +ellps=PZ90 +datum=PZ90 +no_defs"); // PZ-90


// Mapeo de nombres de sistemas a sus códigos EPSG
const systemToEPSG = {
  'WGS84': 'EPSG:4326',
  'UTM ETRS89 (Uso 27)': 'EPSG:25827',
  'UTM ETRS89 (Uso 28)': 'EPSG:25828',
  'UTM ETRS89 (Uso 29)': 'EPSG:25829',
  'UTM ETRS89 (Uso 30)': 'EPSG:25830',
  'UTM ETRS89 (Uso 31)': 'EPSG:25831',
  'UTM ETRS89 (Uso 32)': 'EPSG:25832',
  'ED50 (Lat/Lon)': 'EPSG:4230',
  'UTM ED50 (Uso 27)': 'EPSG:23027',
  'UTM ED50 (Uso 28)': 'EPSG:23028',
  'UTM ED50 (Uso 29)': 'EPSG:23029',
  'UTM ED50 (Uso 30)': 'EPSG:23030',
  'UTM ED50 (Uso 31)': 'EPSG:23031',
  'UTM ED50 (Uso 32)': 'EPSG:23032',
  'Web Mercator': 'EPSG:3857',
  'NAD83 (Lat/Lon)': 'EPSG:4269',
  'GDA94 (Lat/Lon)': 'EPSG:4283',
  'ITRF2008 (Lat/Lon)': 'EPSG:5326',
  'PZ-90 (Lat/Lon)': 'EPSG:4284',
};


export const convertCoordinates = (latitude, longitude, altitude, source, accuracy, targetSystem) => {
  let x, y;
  const z = altitude !== null ? altitude : 0;
  const coordSystem = targetSystem;

  // Coordenadas de origen siempre en WGS84 (EPSG:4326)
  const fromProj = systemToEPSG['WGS84'];
  const toProj = systemToEPSG[targetSystem];

  if (targetSystem === 'WGS84') {
    x = longitude;
    y = latitude;
  } else if (toProj) {
    // Usar proj4 para la transformación
    [x, y] = proj4(fromProj, toProj, [longitude, latitude]);
  } else {
    // Fallback si el sistema no está definido (aunque no debería pasar con el mapeo)
    console.warn(`Sistema de coordenadas no reconocido: ${targetSystem}. Usando WGS84.`);
    x = longitude;
    y = latitude;
  }

  return {
    id: generateUniqueId(),
    x: x,
    y: y,
    z: z,
    source: source,
    accuracy: accuracy,
    coordSystem: coordSystem,
  };
};

export const exportToCsv = (data, filename) => {
  if (data.length === 0) {
    alert("No hay datos para exportar, ¡obtén algunas coordenadas primero!");
    return;
  }

  const headers = ["ID", "X", "Y", "Z", "Origen", "Precisión (m)", "Sistema Coordenadas"];
  const csvRows = [];

  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map(header => {
      switch (header) {
        case "ID": return row.id;
        case "X": return row.x;
        case "Y": return row.y;
        case "Z": return row.z;
        case "Origen": return row.source;
        case "Precisión (m)": return row.accuracy;
        case "Sistema Coordenadas": return row.coordSystem;
        default: return '';
      }
    });
    csvRows.push(values.join(','));
  }

  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};